'use client';

import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, Info, type LucideIcon } from 'lucide-react';

type BannerLevel = 'info' | 'warn' | 'error';
type BannerPayload =
  | { enabled: false }
  | { enabled: true; level: BannerLevel; message: string };

const LEVEL_STYLES: Record<BannerLevel, { container: string; Icon: LucideIcon; role: 'status' | 'alert' }> = {
  info: {
    container: 'bg-sky-500/15 border-sky-500/30 text-sky-700 dark:text-sky-300',
    Icon: Info,
    role: 'status',
  },
  warn: {
    container: 'bg-amber-500/15 border-amber-500/30 text-amber-700 dark:text-amber-400',
    Icon: AlertTriangle,
    role: 'status',
  },
  error: {
    container: 'bg-red-500/15 border-red-500/40 text-red-700 dark:text-red-300',
    Icon: AlertCircle,
    role: 'alert',
  },
};

export function AdminBanner() {
  const { data } = useQuery<BannerPayload>({
    queryKey: ['adminBanner'],
    queryFn: async () => {
      const res = await fetch('/api/banner');
      if (!res.ok) throw new Error(`banner fetch failed: ${res.status}`);
      return (await res.json()) as BannerPayload;
    },
    refetchInterval: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (!data || !data.enabled || !data.message) return null;

  const { container, Icon, role } = LEVEL_STYLES[data.level];

  return (
    <div
      role={role}
      className={`flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-center border-b ${container}`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" aria-hidden />
      <span>{data.message}</span>
    </div>
  );
}
